import LANG from '../lang';

const supportedLanguages = Object.values(LANG).map(({ lng }) => lng);
const GOOGLE_TRANSLATE_STORAGE_KEY = 'google-translate-language';
const GOOGLE_TRANSLATE_COOKIE_NAME = 'googtrans';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const getSupportedLanguage = (language) => {
	if (!language) return LANG.EN.lng;

	const normalizedLanguage = String(language).toLowerCase();
	if (supportedLanguages.includes(normalizedLanguage)) return normalizedLanguage;

	const baseLanguage = normalizedLanguage.split('-')[0];
	if (supportedLanguages.includes(baseLanguage)) return baseLanguage;

	return LANG.EN.lng;
};

const getCookieDomainCandidates = () => {
	if (typeof window === 'undefined') return [];

	const hostname = window.location.hostname;
	if (!hostname || !hostname.includes('.')) return [];

	const parts = hostname.split('.').filter(Boolean);
	const domains = [];

	for (let index = 0; index <= parts.length - 2; index += 1) {
		const domain = parts.slice(index).join('.');
		domains.push(domain, `.${domain}`);
	}

	return [...new Set(domains)];
};

const writeGoogleTranslateCookie = (cookieValue, options = '') => {
	const secure = window.location.protocol === 'https:' ? ';secure' : '';
	document.cookie = `${GOOGLE_TRANSLATE_COOKIE_NAME}=${cookieValue};path=/;max-age=${COOKIE_MAX_AGE};SameSite=Lax${secure}${options}`;
};

const clearGoogleTranslateCookies = () => {
	const secure = window.location.protocol === 'https:' ? ';secure' : '';
	const expiredCookie = `${GOOGLE_TRANSLATE_COOKIE_NAME}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT;max-age=0;SameSite=Lax${secure}`;

	document.cookie = expiredCookie;
	getCookieDomainCandidates().forEach((domain) => {
		document.cookie = `${expiredCookie};domain=${domain}`;
	});
};

const setGoogleTranslateCookie = (lang) => {
	const cookieValue = lang === LANG.EN.lng ? '/auto/en' : `/en/${lang}`;
	clearGoogleTranslateCookies();
	writeGoogleTranslateCookie(cookieValue);

	getCookieDomainCandidates().forEach((domain) => {
		writeGoogleTranslateCookie(cookieValue, `;domain=${domain}`);
	});
};

const getGoogleTranslateCookieLanguage = () => {
	const matchedCookie = document.cookie
		.split('; ')
		.find((cookie) => cookie.startsWith(`${GOOGLE_TRANSLATE_COOKIE_NAME}=`));

	if (!matchedCookie) {
		return LANG.EN.lng;
	}

	const cookieValue = matchedCookie.split('=')[1] || '';
	const targetLanguage = cookieValue.split('/').pop();
	return getSupportedLanguage(targetLanguage);
};

export const syncGoogleTranslateLanguage = async (language) => {
	const nextLanguage = getSupportedLanguage(language);
	localStorage.setItem(GOOGLE_TRANSLATE_STORAGE_KEY, nextLanguage);
	setGoogleTranslateCookie(nextLanguage);
	return nextLanguage;
};

export const changeGoogleTranslateLanguage = async (language) => {
	const nextLanguage = getSupportedLanguage(language);
	const currentLanguage = getGoogleTranslateCookieLanguage();
	const storedLanguage = getStoredLanguage();

	await syncGoogleTranslateLanguage(nextLanguage);

	if (currentLanguage !== nextLanguage || storedLanguage !== nextLanguage) {
		window.setTimeout(() => {
			window.location.reload();
		}, 150);
	}

	return nextLanguage;
};

export const getStoredLanguage = () => {
	return getSupportedLanguage(localStorage.getItem(GOOGLE_TRANSLATE_STORAGE_KEY));
};

export const isGoogleTranslateActive = () => getStoredLanguage() !== LANG.EN.lng;
